import React, { useEffect, useMemo, useState } from "react";
import { useAllServices, useFeaturedServices } from "@/hooks/useHotelServices";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { HotelService, HotelServiceProviderType } from "@/types/hotelService";

type AddServiceDialogType = "recommended" | "featured" | "cta" | "chat";

interface AddServiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (service: HotelService) => void;
  hotelId: string;
  existingIds: string[];
  title: string;
  type?: AddServiceDialogType;
  providerTypes?: HotelServiceProviderType[];
}

export const AddServiceDialog: React.FC<AddServiceDialogProps> = ({
  open,
  onOpenChange,
  onAdd,
  hotelId,
  existingIds,
  title,
  type = "recommended",
  providerTypes,
}) => {
  const { t } = useTranslation();
  const canLoadAllServices =
    type === "featured" ||
    providerTypes === undefined ||
    providerTypes.length > 0;
  const { data: availableServicesResponse, isLoading: isLoadingRecommended } =
    useAllServices(hotelId, providerTypes, canLoadAllServices);
  const { data: featuredServices, isLoading: isLoadingFeatured } =
    useFeaturedServices(hotelId);

  const [search, setSearch] = useState("");
  const isLoading =
    type === "featured" ? isLoadingFeatured : isLoadingRecommended;
  const services = useMemo(
    () =>
      type === "featured"
        ? (featuredServices ?? [])
        : (availableServicesResponse?.data ?? []),
    [availableServicesResponse?.data, featuredServices, type],
  );

  useEffect(() => {
    if (!open) {
      setSearch("");
    }
  }, [open]);

  const filteredServices = useMemo(
    () =>
      services.filter(
        (service) =>
          !existingIds.includes(service.id) &&
          service.name.toLowerCase().includes(search.toLowerCase()),
      ),
    [existingIds, search, services],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="relative">
            <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-zinc-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t(
                "mobileSettings.dashboard.searchServices",
                "Search services...",
              )}
              className="pl-9"
            />
          </div>

          <div className="custom-scrollbar h-[400px] overflow-y-auto pr-4">
            {isLoading && (
              <div className="py-8 text-center text-zinc-500">
                {t(
                  "mobileSettings.dashboard.loadingServices",
                  "Loading services...",
                )}
              </div>
            )}
            {!isLoading && filteredServices.length === 0 && (
              <div className="py-8 text-center text-zinc-500">
                {t(
                  "mobileSettings.dashboard.noServicesFound",
                  "No services found",
                )}
              </div>
            )}
            {!isLoading && filteredServices.length > 0 && (
              <div className="grid grid-cols-2 gap-3">
                {filteredServices.map((service) => (
                  <button
                    type="button"
                    key={service.id}
                    className="flex cursor-pointer flex-col gap-2 rounded-xl border bg-zinc-50/50 p-3 text-left transition-colors hover:border-zinc-900"
                    onClick={() => {
                      onAdd(service);
                      onOpenChange(false);
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <Badge variant="new_secondary">
                        {service.category.name}
                      </Badge>
                      <span className="text-xs font-bold">
                        {service.price ?? 0}€
                      </span>
                    </div>
                    <span className="line-clamp-2 text-sm font-medium">
                      {service.name}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
